class EducationToggle extends HTMLElement {
  constructor() {
    super()
    this.eduState = localStorage.getItem('isEDU')
    this.isEDUDocumentLoaded = localStorage.getItem('EDUDocument')
    this.isEDUFormLoaded = false
    this.formError = false
    this.selectors = {
      eduPrice: '[data-edu-price]',
      regularPrice: '[data-regular-price]',
      toggle: '[data-edu-toggle]',
      input: '#education',
      popup: '[data-popup]',
      errorPopup: '[data-warning-popup]',
      closePopup: '[data-popup-close]',
      popupOverlay: '[data-popup-overlay]',
      cartObj: '[data-cart-items]'
    }
    this.isCartPage = this.dataset.page == 'cart' ? true : false
    this.querySelector(this.selectors.input).checked = this.eduState
    this.querySelector(this.selectors.toggle).addEventListener('click', this.handleToggleClick.bind(this))
    this.querySelectorAll(this.selectors.closePopup).forEach(el => el.addEventListener('click', this.closePopup.bind(this)))
    this.querySelectorAll(this.selectors.popupOverlay).forEach(el => el.addEventListener('click', this.closePopup.bind(this)))
    this.querySelector('.education-toggle__popup-selection').addEventListener('click', this.handleChangeFile.bind(this))
    document.addEventListener('globo.formbuilder.form.loaded', () => {
      this.isEDUFormLoaded = true
      this.prepareUpload()
	  })
    this.changePrices(this.eduState)
    this.init()
  }

  async init() {
    this.watchForDocumentUrl()
    this.watchPriceMarkup()
    await this.syncWithLiveCart()
    this.querySelector(this.selectors.input).checked = !!this.eduState
    this.changeDefaultState()
  }

  // The theme re-renders the price markup on every variant change (the section is
  // fetched again and the EDU block always comes back hidden), so the toggle state,
  // which lives here and not in the markup, is re-applied to the new prices.
  watchPriceMarkup() {
    if (this.priceMarkupWatched) return
    this.priceMarkupWatched = true
    const resync = this.schedulePriceSync.bind(this)

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, resync)
    }
    // the configurable (CTO) products swap their prices without going through the pub/sub
    document.addEventListener('variant:changed', resync)

    // catches every other path that rewrites the prices: the sticky bar, the monthly
    // pricing, an app replacing the price element
    const priceSelector = `${this.selectors.eduPrice}, ${this.selectors.regularPrice}`
    this.priceObserver = new MutationObserver((mutations) => {
      // a toggle taken out of the page, e.g. by a section re-render, stops watching
      if (!this.isConnected) {
        this.priceObserver.disconnect()
        return
      }
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue
          if (node.matches(priceSelector) || node.querySelector(priceSelector)) {
            resync()
            return
          }
        }
      }
    })
    this.priceObserver.observe(document.body, { childList: true, subtree: true })
  }

  // the prices are rewritten in several steps within the same frame, one sync after
  // them is enough
  schedulePriceSync() {
    if (this.priceSyncPending) return
    this.priceSyncPending = true
    requestAnimationFrame(() => {
      this.priceSyncPending = false
      if (!this.isConnected) return
      this.changePrices(this.eduState)
    })
  }

  get cartData() {
    if (!this._cartData) {
      try {
        this._cartData = JSON.parse(this.querySelector(this.selectors.cartObj).innerHTML)
      } catch (error) {
        console.error('[EDU] Could not read the cart data', error)
        this._cartData = { cart: { items: [] }, items: {}, currentDiscount: '' }
      }
    }
    return this._cartData
  }

  handleToggleClick(event) {
    event.stopPropagation()
    event.preventDefault()
    this.openPopup(this.selectors.popup)
    this.showUploadForm()
    // the prices in the markup can be newer than the toggle, e.g. after a variant change
    this.changePrices(this.eduState)
  }

  checkFile(event) {
    if (event) event.preventDefault()
    if (this.eduState && !this.isEDUDocumentLoaded) {
      this.openPopup(this.selectors.errorPopup)
      return
    }
    if (!this.eduState) {
      window.location.href= `${window.Shopify.routes.root}checkout`
      return
    }
    this.saveEDUDetails().then(() => {
      window.location.href= `${window.Shopify.routes.root}checkout`
    });
  }

  async saveEDUDetails() {
    const fileData = this.eduDocument()
    if (!fileData) return
    const cart = await this.updateCartAttributes({
      'EDU reference': fileData.reference || '',
      'EDU document': fileData.url || fileData.name || '',
      'EDU submitted': fileData.time || '',
      'EDU contact': fileData.contact || ''
    })

    if (cart && cart.token) {
      this.cartToken = cart.token
      if (this.cartHasItems(cart)) this.bindDocumentToCart(cart.token)
    }
    if (cart && this.debugEnabled()) console.log('[EDU] cart attributes after saving', cart.attributes)
    return cart
  }

  async clearEDUDetails() {
    return this.updateCartAttributes({
      'EDU reference': '',
      'EDU document': '',
      'EDU submitted': '',
      'EDU contact': ''
    })
  }

  legacyNoteReset() {
    const note = this.cartData.cart ? this.cartData.cart.note : null
    if (typeof note !== 'string') return {}
    return /^Document name:.*Submit time:/i.test(note.trim().replace(/\s+/g, ' ')) ? { note: '' } : {}
  }

  // an empty value removes the attribute from the cart, the updated cart is returned
  async updateCartAttributes(attributes) {
    return fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(Object.assign({ attributes: attributes }, this.legacyNoteReset()))
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[EDU] Could not save the document details to the cart', error)
        return null
      })
  }

  eduDocument() {
    try {
      return JSON.parse(localStorage.getItem('EDUDocument'))
    } catch (error) {
      return null
    }
  }

  // The cart the browser actually has. The token rendered by Liquid comes from markup
  async fetchLiveCart(force) {
    if (this._liveCart && !force) return this._liveCart
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { 'Accept': 'application/json' } })
      this._liveCart = await response.json()
    } catch (error) {
      console.warn('[EDU] Could not read the cart', error)
      this._liveCart = null
    }
    return this._liveCart
  }

  async syncWithLiveCart() {
    const cart = await this.fetchLiveCart(true)
    this.cartToken = (cart && cart.token) || this.cartData.cartToken || ''
    const fileData = this.eduDocument()
    if (!fileData || !cart) return
    if (this.cartWasCheckedOut(cart, fileData)) {
      await this.dropDocument(cart)
      return
    }
    // uploaded before there was a cart to bind it to
    if (!fileData.cartToken && this.cartHasItems(cart)) this.bindDocumentToCart(this.cartToken)
    if (this.attributesMissing(cart)) await this.saveEDUDetails()
  }

  // Shopify hands out a fresh token for every empty cart, so a token only identifies a
  // cart once it holds a line. Binding to an empty cart's token would make the document
  // look like it belonged to someone else's cart on the very next page the customer opens.
  cartHasItems(cart) {
    if (!cart) return false
    return cart.item_count > 0 || (cart.items || []).length > 0
  }

  // The document is dropped when the cart it was bound to is gone and the customer is
  // back to an empty one: the order went through (Shopify clears the cart after
  // checkout) or the cart expired. A different token that still holds lines is the same
  // shopper's cart under a new id, e.g. after logging in, and keeps the document.
  cartWasCheckedOut(cart, fileData) {
    if (!fileData.cartToken || !this.cartToken) return false
    if (fileData.cartToken === this.cartToken) return false
    return !this.cartHasItems(cart)
  }

  attributesMissing(cart) {
    const fileData = this.eduDocument()
    if (!fileData) return false
    const attributes = (cart && cart.attributes) || {}
    return attributes['EDU document'] !== (fileData.url || fileData.name || '')
      || attributes['EDU reference'] !== (fileData.reference || '')
  }

  // written again as soon as a line is added, the moment the cart starts keeping them
  async reassertAttributes() {
    if (!this.eduDocument() || this.reassertPending) return
    this.reassertPending = true
    try {
      const cart = await this.fetchLiveCart(true)
      this.cartToken = (cart && cart.token) || this.cartToken || ''
      if (this.cartHasItems(cart)) this.bindDocumentToCart(this.cartToken)
      if (this.attributesMissing(cart)) await this.saveEDUDetails()
      // the codes are applied to the cart the customer now has: a code applied while
      // the cart was still empty is not carried over by Shopify
      await this.setDiscount()
    } finally {
      this.reassertPending = false
    }
  }

  async dropDocument(cart) {
    this.forgetDocument()
    this.resetDocumentUI()
    const attributes = (cart && cart.attributes) || {}
    if (attributes['EDU document'] || attributes['EDU reference']) await this.clearEDUDetails()
  }

  resetDocumentUI() {
    const checkbox = this.querySelector(this.selectors.input)
    if (checkbox) checkbox.checked = false
    this.classList.remove('has-document')
    const success = this.querySelector('.education-toggle__popup-text--success')
    if (success) success.classList.add('hidden')
    const selection = this.querySelector('.education-toggle__popup-selection')
    if (selection) selection.innerHTML = ''
    const formFooter = this.querySelector('.edu-form .wizard__footer')
    const formInput = this.querySelector('.edu-form .globo-form-app .panel')
    if (formFooter) formFooter.classList.remove('hidden')
    if (formInput) formInput.classList.remove('hidden')
    this.changePrices(false)
  }

  // empty while the cart holds nothing, so a document uploaded before anything is added
  // stays valid across the shop instead of being tied to a token Shopify throws away
  bindableCartToken() {
    if (!this.cartHasItems(this._liveCart)) return ''
    return this.cartToken || this.cartData.cartToken || ''
  }

  bindDocumentToCart(token) {
    const fileData = this.eduDocument()
    if (!token || !fileData || fileData.cartToken === token) return
    fileData.cartToken = token
    localStorage.setItem('EDUDocument', JSON.stringify(fileData))
    this.isEDUDocumentLoaded = localStorage.getItem('EDUDocument')
  }

  forgetDocument() {
    localStorage.removeItem('EDUPreview')
    localStorage.removeItem('EDUDocument')
    localStorage.removeItem('EDUDocumentName')
    localStorage.removeItem('isEDU')
    this.eduState = null
    this.isEDUDocumentLoaded = null
  }

  async changeDefaultState() {
    this.changePrices(this.eduState)
    await this.setDiscount()
    this.waitingForFormSubmit()
    this.changeForm()
  }

  showUploadForm() {
    if (this.isEDUFormLoaded) {
      this.classList.add('active')
      this.prepareUpload()
      this.bindFileInput()
      this.changeForm()
      return
    }
    let attempts = 0
    let maxAttempts = 10
    const checkFormInterval = setInterval(() => {
      attempts++;
      const form = document.querySelector('.globo-formbuilder .edu-form');

      if (form) {
        clearInterval(checkFormInterval);
        this.isEDUFormLoaded = true;
        this.classList.add('active');
        this.prepareUpload()
        this.bindFileInput()
        this.changeForm()
      } else if (attempts >= maxAttempts) {
        clearInterval(checkFormInterval);
        return
      }
    }, 500);
  }

  bindFileInput() {
    const input = this.querySelector('.globo-formbuilder .edu-form__input input')
      || this.querySelector('.globo-formbuilder input[type="file"]')
    if (!input || input.eduBound) return
    input.eduBound = true
    input.addEventListener('change', this.handleFormInputChange.bind(this))
  }

  changePrices(state) {
    const eduPrices = document.querySelectorAll(this.selectors.eduPrice)
    const regularPrices = document.querySelectorAll(this.selectors.regularPrice)
    if (state) {
      this.querySelector('.education-toggle__popup-text--success').classList.remove('hidden')
      if (eduPrices.length == 0 || regularPrices.length == 0) return
      eduPrices.forEach(el => {el.classList.remove('hidden')})
      regularPrices.forEach(el => {el.classList.add('hidden')})
    } else {
      if (eduPrices.length == 0 || regularPrices.length == 0) return
      eduPrices.forEach(el => {el.classList.add('hidden')})
      regularPrices.forEach(el => {el.classList.remove('hidden')})
    }
  }

  checkEDUDocument() {
    if (this.eduState && !this.isEDUDocumentLoaded) {
      this.openPopup(this.selectors.errorPopup)
      return false
    }
    return true
  }

  async setDiscount() {
    // the live cart when it has been read, it knows about the codes applied since the
    // page was rendered
    const allCartDiscounts = this.checkCartDiscounts(this._liveCart)
    if (!this.eduState) {
      const removeObj = this.removeEDUDiscounts(allCartDiscounts)
      if (removeObj[1]) await this.applyCodes(removeObj[0])
      return
    }
    if (!this.isEDUDocumentLoaded) return
    const codes = this.addEDUDiscounts()
    // nothing to apply, e.g. an empty cart or products without an EDU discount
    if (!codes) return
    if (this.eduCodesApplied(codes, allCartDiscounts)) return
    await this.applyCodes(codes)
  }

  eduCodesApplied(codes, cartDiscounts) {
    // only the EDU codes matter, other campaigns in the cart must not trigger a re-apply
    const applied = cartDiscounts.map(title => title.toLowerCase())
    return codes.split(',').every(code => applied.includes(code.toLowerCase()))
  }

  async applyCodes(codes) {
    await fetch(`${window.shopUrl}/checkout?discount=${codes.split(',').map(encodeURIComponent).join(',')}`)
      .then((res) => { return res.text()})
      .then(() => {
        if (!this.isCartPage) return
        if (this.canReloadCart(codes)) {
          window.location.href = window.location.href
          return
        }
        if (codes) {
          console.warn(`[EDU] Shopify did not apply "${codes}". Check that a discount code with this exact name exists, is active, combines with the other discounts in the cart and covers these products.`)
        }
      });
  }

  // otherwise leave the page reloading in a loop
  canReloadCart(codes) {
    const cart = this.cartData.cart || {}
    const state = [codes, cart.item_count, cart.total_price, cart.total_discount].join('|')
    try {
      if (sessionStorage.getItem('EDUCartReload') === state) return false
      sessionStorage.setItem('EDUCartReload', state)
    } catch (error) {
      return true
    }
    return true
  }

  addEDUDiscounts() {
    // codes are rendered by the `edu-discount-code` snippet, empty when a product has no EDU discount
    const cartObj = this.cartData
    const discounts = []
    if (!this.isCartPage && cartObj.currentDiscount) {
      discounts.push(cartObj.currentDiscount)
    }
    const items = cartObj.items
    for (const key in items) {
      const discount = items[key]
      if (discount && !discounts.includes(discount)) {
        discounts.push(discount)
      }
    }
    return discounts.toString()
  }

  removeEDUDiscounts(discounts) {
    let newDiscountsArray = []
    let wasEDU = false
    if (!discounts || discounts.length == 0) return ['', false]
    for (let i = 0; i < discounts.length; i++) {
      if (discounts[i].indexOf('EDU-') == -1) {
        newDiscountsArray.push(discounts[i])
      } else {
        wasEDU = true
      }
    }
    return [newDiscountsArray.toString(), wasEDU]
  }

  checkCartDiscounts(liveCart) {
    const cart = liveCart || this.cartData.cart || {}
    const qniqDiscounts = []
    const addTitle = (title) => {
      if (title && !qniqDiscounts.includes(title)) qniqDiscounts.push(title)
    }
    for (const application of cart.cart_level_discount_applications || []) {
      addTitle(application?.title)
    }
    for (const item of cart.items || []) {
      // `discounts` is the legacy field, `line_level_discount_allocations` the current one
      for (const discount of item.discounts || []) {
        addTitle(discount?.title)
      }
      for (const allocation of item.line_level_discount_allocations || []) {
        addTitle(allocation?.discount_application?.title)
      }
    }
    return qniqDiscounts
  }

  waitingForFormSubmit() {
    document.addEventListener('globo.formbuilder.form.beforeSubmit', this.checkFormError.bind(this))
    document.addEventListener('globo.formbuilder.form.submitting', this.handleFormSubmit.bind(this))
  }

  handleFormInputChange(event) {
    const input = (event && event.target) || this.querySelector('.globo-formbuilder .edu-form__input input')
    const formInputData = input && input.files ? input.files[0] : null
    if (!formInputData) return
    localStorage.setItem('EDUDocumentName', formInputData.name)
    this.pendingFileName = formInputData.name
    this.sweepFormForDocumentUrl()
    const reader = new FileReader()
    reader.onload = () => {
      localStorage.setItem('EDUPreview', reader.result);
    }
    reader.readAsDataURL(formInputData);

  }

  checkFormError() {
    this.formError = false
    const form = document.querySelector('.globo-formbuilder')
    const formError = form ? form.querySelector('.block-container.errors') : null
    if (formError) {
      this.formError = true
      return
    }
    this.recordSubmission()
  }

  // Stores the uploaded document. Called from the form app's events when it dispatches
  recordSubmission() {
    const existing = this.eduDocument() || {}
    const form = document.querySelector('.globo-formbuilder')
    const fileData = {
      name: localStorage.getItem('EDUDocumentName') || existing.name || 'Document',
      time: existing.time || this.currentTimestamp(),
      reference: existing.reference || this.pendingReference || this.buildReference(),
      contact: existing.contact || (form ? this.formContact(form) : ''),
      url: this.pendingDocumentUrl || existing.url || '',
      cartToken: existing.cartToken || this.bindableCartToken()
    }
    localStorage.setItem('EDUDocument', JSON.stringify(fileData))
    localStorage.removeItem('EDUDocumentName');
    this.isEDUDocumentLoaded = localStorage.getItem('EDUDocument')
    localStorage.setItem('isEDU', true)
    this.eduState = true
    return fileData
  }

  // everything that has to happen once a document is accepted, in one place
  async completeSubmission() {
    this.recordSubmission()
    this.sweepFormForDocumentUrl()
    this.querySelector(this.selectors.input).checked = true
    this.querySelector('.education-toggle__popup-text--success').classList.remove('hidden')
    this.changePrices(true)
    this.changeForm()
    // the details go to the cart before the discount: applying a code reloads the cart
    // page, which would cut a pending cart request short
    await this.saveEDUDetails()
    await this.setDiscount()
  }

  currentTimestamp() {
    const date = new Date()
    const pad = (value) => String(value).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  // short id shared by the order and the form submission, so the two can be matched
  buildReference() {
    const random = Math.random().toString(36).slice(2, 6).toUpperCase()
    return `EDU-${Date.now().toString(36).toUpperCase()}-${random}`
  }

  // everything that has to be in place before the customer uploads a document
  prepareUpload() {
    this.watchForDocumentUrl()
    this.prepareReference()
  }

  // fills the form's reference field when the form has one, so the submission in the
  // form app carries the same id as the order
  prepareReference() {
    this.pendingReference = this.buildReference()
    const field = this.querySelector('.globo-formbuilder [data-edu-reference], .globo-formbuilder input[name*="reference" i]')
    if (field) field.value = this.pendingReference
  }

  formContact(form) {
    const email = form.querySelector('input[type="email"], input[name*="email" i]')
    return email && email.value ? email.value.trim() : ''
  }

  watchForDocumentUrl() {
    if (this.documentUrlWatched) return
    this.documentUrlWatched = true
    try {
      this.interceptFormAppRequests()
    } catch (error) {
      // capturing the URL is a bonus, it must never break the upload itself
      console.warn('[EDU] Could not watch for the document URL', error)
    }
  }

  shouldScanResponse(url) {
    const target = String(url || '')
    if (/\/cart\/|\/checkout|monorail|analytics|\.js($|\?)|\.css($|\?)/i.test(target)) return false
    if (/globo|formbuilder/i.test(target)) return true
    return !!(this.pendingFileName || localStorage.getItem('EDUDocumentName')) && !this.pendingDocumentUrl
  }

  uploadedFileName() {
    const stored = this.eduDocument()
    return localStorage.getItem('EDUDocumentName') || this.pendingFileName || (stored && stored.name) || ''
  }

  interceptFormAppRequests() {
    const shouldScan = (url) => this.shouldScanResponse(url)
    const scan = (text, url) => {
      if (this.debugEnabled()) console.log('[EDU] response from', url, String(text).slice(0, 800))
      this.handleFormAppResponse(text, url)
    }

    const scanRequest = (body, url) => {
      if (!shouldScan(url)) return
      const text = this.readRequestBody(body)
      if (!text) return
      if (this.debugEnabled()) console.log('[EDU] request to', url, text.slice(0, 800))
      this.handleFormAppPayload(text, url)
    }
    const isCartAdd = (url) => /\/cart\/add(\.js)?(\?|$)/i.test(String(url || ''))
    const onCartAdd = () => this.reassertAttributes()

    const originalFetch = window.fetch
    window.fetch = function (...args) {
      const request = args[0]
      const url = typeof request === 'string' ? request : (request && request.url) || ''
      const options = args[1] || {}
      try { scanRequest(options.body, url) } catch (error) { /* body already consumed */ }
      const response = originalFetch.apply(this, args)
      if (shouldScan(url)) {
        response.then(res => res.clone().text().then(text => scan(text, url))).catch(() => {})
      }
      if (isCartAdd(url)) response.then(res => { if (res.ok) onCartAdd() }).catch(() => {})
      return response
    }

    const originalOpen = XMLHttpRequest.prototype.open
    const originalSend = XMLHttpRequest.prototype.send
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.eduRequestUrl = url
      return originalOpen.call(this, method, url, ...rest)
    }
    XMLHttpRequest.prototype.send = function (...args) {
      try { scanRequest(args[0], this.eduRequestUrl) } catch (error) { /* unreadable body */ }
      if (shouldScan(this.eduRequestUrl)) {
        this.addEventListener('load', () => {
          try { scan(this.responseText, this.eduRequestUrl) } catch (error) { /* opaque response */ }
        })
      }
      if (isCartAdd(this.eduRequestUrl)) {
        this.addEventListener('load', () => { if (this.status >= 200 && this.status < 300) onCartAdd() })
      }
      return originalSend.apply(this, args)
    }
  }

  // only the readable bodies, a raw file upload carries no URL to find anyway
  readRequestBody(body) {
    if (!body) return ''
    if (typeof body === 'string') return body
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return body.toString()
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const parts = []
      body.forEach((value) => { if (typeof value === 'string') parts.push(value) })
      return parts.join(' ')
    }
    return ''
  }

  // a request payload confirms nothing about the submission itself, it is read for the
  // document URL alone
  handleFormAppPayload(text, requestUrl) {
    const fromFormApp = /globo|formbuilder/i.test(String(requestUrl || ''))
    const url = this.findDocumentUrl(text, !fromFormApp)
    if (url) this.setDocumentUrl(url)
  }

  // add ?edu_debug=1 to the URL, or set window.EDU_DEBUG, to log what the form app answers
  debugEnabled() {
    try {
      return !!window.EDU_DEBUG || /[?&]edu_debug=1/.test(window.location.search || '')
    } catch (error) {
      return false
    }
  }

  sweepFormForDocumentUrl() {
    if (this.pendingDocumentUrl) return
    const delays = [500, 1500, 3000, 6000, 10000, 15000]
    delays.forEach((delay, index) => setTimeout(() => {
      if (this.pendingDocumentUrl) return
      const form = document.querySelector('.globo-formbuilder')
      if (!form) return
      // the file the customer picked is what is looked for, a link elsewhere in the form
      // is only taken on the last attempt, when nothing else has turned up
      const lastAttempt = index === delays.length - 1
      const found = this.findDocumentUrl(this.formDocumentText(form), !lastAttempt)
      if (found) {
        this.setDocumentUrl(found)
        return
      }
      if (this.debugEnabled()) console.log('[EDU] no document URL in the form markup yet')
    }, delay))
  }

  formDocumentText(form) {
    const values = Array.from(form.querySelectorAll('input, textarea')).map(el => el.value || '')
    const links = Array.from(form.querySelectorAll('a[href], img[src]'))
      .map(el => el.getAttribute('href') || el.getAttribute('src') || '')
    return values.concat(links, [form.innerHTML]).join(' ')
  }

  handleFormAppResponse(text, requestUrl) {
    if (!text || typeof text !== 'string') return
    const fromFormApp = /globo|formbuilder/i.test(String(requestUrl || '')) || requestUrl === undefined
    const url = this.findDocumentUrl(text, !fromFormApp)
    if (url) this.setDocumentUrl(url)
    if (this.formError || !fromFormApp) return
    const accepted = url || /"?(?:success|submitted)"?\s*[":]\s*(?:true|1|"success")/i.test(text)
    if (!accepted || this.eduDocument()) return
    this.completeSubmission()
  }

  findDocumentUrl(text, requireNameMatch) {
    if (!text || typeof text !== 'string') return null
    const unescaped = text.replace(/\\+\//g, '/')
    const searchText = `${unescaped} ${this.decodeSafely(unescaped).replace(/&/g, ' ')}`
    const byExtension = searchText.match(/https?:\/\/[^"'\s\\)<>]+\.(?:png|jpe?g|gif|webp|heic|heif|pdf|svg)(?:\?[^"'\s\\)<>]*)?/gi) || []
    const byPath = searchText.match(/https?:\/\/[^"'\s\\)<>]*(?:\/uploads?\/|\/files\/)[^"'\s\\)<>]+/gi) || []
    const urls = byExtension.concat(byPath.filter(url => byExtension.indexOf(url) === -1))
      .filter(url => !/\/products?\//i.test(url))
    if (!urls.length) return null
    const uploaded = this.uploadedFileName()
    const stem = uploaded.replace(/\.[^.]+$/, '').replace(/[^a-z0-9]+/gi, '').toLowerCase().slice(0, 12)
    const normalise = (url) => decodeURIComponent(url).replace(/[^a-z0-9]+/gi, '').toLowerCase()
    const named = urls.find(url => stem && normalise(url).includes(stem))
    if (requireNameMatch) return named || null
    return named || urls[0]
  }

  // a form encoded request carries the URL percent encoded, and a body that is not
  // decodable as a whole still gives up its slashes and colons
  decodeSafely(text) {
    if (!/%[0-9a-f]{2}/i.test(text)) return ''
    try {
      return decodeURIComponent(text.replace(/\+/g, ' '))
    } catch (error) {
      return text.replace(/%2F/gi, '/').replace(/%3A/gi, ':')
    }
  }

  setDocumentUrl(url) {
    this.pendingDocumentUrl = url
    const fileData = this.eduDocument()
    if (!fileData || fileData.url === url) return
    fileData.url = url
    localStorage.setItem('EDUDocument', JSON.stringify(fileData))
    this.isEDUDocumentLoaded = localStorage.getItem('EDUDocument')
    this.saveEDUDetails()
  }

  async handleFormSubmit(event) {
    if (!this.formError) await this.completeSubmission()
  }

  changeForm() {
    const fileData = this.eduDocument()
    if (!fileData) return
    const preview = localStorage.getItem('EDUPreview');
    const formFooter = this.querySelector('.edu-form .wizard__footer')
    const formInput = this.querySelector('.edu-form .globo-form-app .panel')
    const userSelectionWrapper = this.querySelector('.education-toggle__popup-selection')
    userSelectionWrapper.innerHTML = ''
    this.classList.add('has-document')
    if (formFooter) formFooter.classList.add('hidden')
    if (formInput) formInput.classList.add('hidden')
    const content = `<span class="block-title">Document uploaded</span>
    ${preview ? `<img src="${preview}" />` : ''}
    <span>File: ${fileData.name}</span>
    ${fileData.reference ? `<span>Reference: ${fileData.reference}</span>` : ''}
    <div class="education-toggle__popup-btn-wrapper">
    <div class="education-toggle__popup-change-btn">Change File</div>
    <div class="education-toggle__popup-remove-btn">Remove File</div>
    </div>`
    userSelectionWrapper.innerHTML = content
  }

  handleChangeFile(event) {
    const btn = event.target.classList.contains('education-toggle__popup-change-btn') ? event.target : null
    const removeBtn = event.target.classList.contains('education-toggle__popup-remove-btn') ? event.target : null
    if (removeBtn) {
     this.handleRemoveFile()
     return
    }
    if (!btn) return
    this.querySelector('.education-toggle__popup-text--success').classList.remove('hidden')
    this.classList.remove('has-document')
    const formFooter = this.querySelector('.edu-form .wizard__footer')
    const formInput = this.querySelector('.edu-form .globo-form-app .panel')
    const userSelectionWrapper = this.querySelector('.education-toggle__popup-selection')
    userSelectionWrapper.innerHTML = ''
    if (formFooter) formFooter.classList.remove('hidden')
    if (formInput) formInput.classList.remove('hidden')
  }

  async handleRemoveFile() {
    this.querySelector(this.selectors.input).checked = false
    this.forgetDocument()
    this.eduState = false
    this.querySelector('.education-toggle__popup-text--success').classList.add('hidden')
    this.classList.remove('has-document')
    const formFooter = this.querySelector('.edu-form .wizard__footer')
    const formInput = this.querySelector('.edu-form .globo-form-app .panel')
    const userSelectionWrapper = this.querySelector('.education-toggle__popup-selection')
    userSelectionWrapper.innerHTML = ''
    if (formFooter) formFooter.classList.remove('hidden')
    if (formInput) formInput.classList.remove('hidden')
    this.querySelector('[data-popup]').classList.remove('active')
    this.changePrices(this.eduState)
    await this.setDiscount()
    await this.clearEDUDetails()
  }

  closePopup(event) {
    const target = event.target.classList.contains('education-toggle__popup') ? event.target : event.target.closest('.education-toggle__popup')
    if (!this.eduState && target.hasAttribute('data-popup')) {
      this.querySelector(this.selectors.input).checked = false
    }
    target.classList.remove('active')
  }
  
  openPopup(selector) {
    this.querySelector(selector).classList.add('active')
  }
}

customElements.define("education-toggle", EducationToggle);